import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  Typography,
  Box,
  Tooltip
} from '@mui/material';
import { CheckCircle, Cancel, Repeat, CheckCircleOutline } from '@mui/icons-material';
import { getQuestionTypeLabel } from '../../utils/questionUtils';

/**
 * DetectedKeysTable - Table of auto-detected valid keys
 * 
 * UX Philosophy:
 * - NO "Ana Soru Ekle" button - Only show detected keys
 * - Key column: readonly (from content, can't be changed)
 * - Action: "Ekle" (not defined) or "Güncelle" (already defined)
 * - Support temp IDs (new templates) and database IDs (existing)
 * - Orphan cleanup handled by parent (TemplateEditorPage)
 * 
 * Usage:
 * ```jsx
 * <DetectedKeysTable
 *   validKeys={getValidKeys(content)}
 *   questions={questions}
 *   onAdd={(key) => openModal({ key, isReadonly: true })}
 *   onEdit={(question) => openModal(question)}
 * />
 * ```
 */
const DetectedKeysTable = ({
  validKeys = [],
  questions = [],
  onAdd,
  onEdit
}) => {
  // No valid keys found (static template)
  if (validKeys.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="body2">
          İçerikte hiç {'{{key}}'} bulunamadı. 
          Statik şablon oluşturuyorsanız bu normaldir.
        </Typography>
      </Alert>
    );
  }
  
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Key</TableCell>
            <TableCell>Soru Metni</TableCell>
            <TableCell>Tip</TableCell>
            <TableCell align="center">Zorunlu</TableCell>
            <TableCell align="center">Çoklu</TableCell>
            <TableCell align="right">İşlem</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {validKeys.map((key) => {
            // Find existing question for this key
            const question = questions.find(q => q.key === key);
            const isDefined = !!question;
            const isRequired = question?.required || false;
            const isRepeatable = question?.repeatable || false;
            
            return (
              <TableRow key={key} hover>
                {/* Key Column (readonly, from content) */}
                <TableCell>
                  <Chip
                    label={key}
                    size="small"
                    sx={{ 
                      fontFamily: 'monospace', 
                      fontWeight: 'bold'
                    }}
                    color={isDefined ? 'info' : 'default'}
                    variant={isDefined ? 'filled' : 'outlined'}
                  />
                </TableCell>
                
                {/* DisplayText Column */}
                <TableCell>
                  <Typography 
                    variant="body2"
                    color={isDefined ? 'text.primary' : 'text.disabled'}
                  >
                    {isDefined ? question.displayText : '-'}
                  </Typography>
                </TableCell>
                
                {/* Type Column */}
                <TableCell>
                  {isDefined ? (
                    <Chip
                      label={getQuestionTypeLabel(question.type)}
                      size="small"
                      color="primary"
                    />
                  ) : (
                    <Typography variant="body2" color="text.disabled">-</Typography>
                  )}
                </TableCell>
                
                {/* Required Column */}
                <TableCell align="center">
                  {isDefined ? (
                    <Tooltip title={isRequired ? 'Zorunlu alan' : 'Opsiyonel alan'}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {isRequired ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="disabled" fontSize="small" />
                        )}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.disabled">-</Typography>
                  )}
                </TableCell>
                
                {/* Repeatable Column */}
                <TableCell align="center">
                  {isDefined ? (
                    <Tooltip title={isRepeatable ? 'Çoklu satır (Excel import/export)' : 'Tek satır'}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {isRepeatable ? (
                          <Repeat color="primary" fontSize="small" />
                        ) : (
                          <CheckCircleOutline color="disabled" fontSize="small" />
                        )}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.disabled">-</Typography>
                  )}
                </TableCell>
                
                {/* Action Column */}
                <TableCell align="right">
                  {isDefined ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => onEdit(question)}
                    >
                      Güncelle
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => onAdd(key)}
                    >
                      Ekle
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

DetectedKeysTable.propTypes = {
  validKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  questions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
};

export default DetectedKeysTable;

